import type { EcrituresRewrite } from '@cpce/shared';
import { Info, TriangleAlert } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button } from './Button';
import { InfoCard } from './InfoCard';
import { describeKeptEcriture } from './KeptEcrituresIndicator';
import { Modal } from './Modal';

interface ConfirmModalProps {
  title: string;
  info?: { title: string; text: ReactNode };
  children?: ReactNode;
  /** A rewrite that kept écritures lists them before the modal closes. */
  onConfirm: (() => Promise<EcrituresRewrite | undefined>) | (() => Promise<void>);
  disabled?: boolean;
  confirmLabel?: string;
  closeModal: () => void;
}

export function ConfirmModal(props: ConfirmModalProps) {
  const {
    title,
    info,
    children,
    onConfirm,
    disabled,
    confirmLabel = 'Confirmer',
    closeModal,
  } = props;
  const [isPending, setIsPending] = useState(false);
  const [rewrite, setRewrite] = useState<EcrituresRewrite | null>(null);

  const confirm = async () => {
    setIsPending(true);
    try {
      const result = await onConfirm();
      if (result && result.keptEntries.length > 0) setRewrite(result);
      else closeModal();
    } finally {
      setIsPending(false);
    }
  };

  if (rewrite) {
    return (
      <Modal open onOpenChange={closeModal} title={title}>
        <InfoCard title="Écritures restées sur l'ancien compte" color="orange" icon={TriangleAlert}>
          <p className="text-sm">
            {rewrite.movedEntriesCount} ligne(s) déplacée(s). Celles-ci n'ont pas été déplacées :
          </p>
          <ul className="text-sm list-disc pl-4">
            {rewrite.keptEntries.map((entry) => (
              <li key={`${entry.transactionId}-${entry.accountCode}`}>
                {describeKeptEcriture(entry)}
              </li>
            ))}
          </ul>
        </InfoCard>
        <Button variant="color" onClick={closeModal}>
          Fermer
        </Button>
      </Modal>
    );
  }

  return (
    <Modal open onOpenChange={closeModal} title={title}>
      {info && (
        <InfoCard title={info.title} color="blue" icon={Info}>
          <p className="text-sm text-gray-700">{info.text}</p>
        </InfoCard>
      )}
      {children}
      <div className="flex gap-2 grow mt-2">
        <Button variant="outline" className="grow" disabled={isPending} onClick={closeModal}>
          Annuler
        </Button>
        <Button
          variant="color"
          className="grow"
          isPending={isPending}
          disabled={disabled}
          onClick={confirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
